<script setup lang="ts">
import { useNuxtApp } from '@nuxt/bridge/dist/runtime'
import { ref, computed, onMounted, onBeforeMount } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/useUserStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useAddressStore } from '@/stores/useAddressStore'
import { useConsumableStore } from '@/stores/useConsumableStore'
import { useMemoriaMaster } from '@/stores/useMemoriaMaster'
import { useWalletStore } from '@/stores/useWalletStore'
import FtAbi from '@/assets/contracts/FtAbi.json'
import ItemPurchaseAbi from '@/assets/contracts/ItemPurchaseAbi.json'
import Nl2br from 'vue-nl2br'
const Modal = () => import('@/components/organisms/Modal.vue')
const DropRateList = () => import('@/components/organisms/DropRateList.vue')
const LotMemoriaResult = () => import('@/components/organisms/LotMemoriaResult.vue')
const MintSelector = () => import('@/components/organisms/MintSelector.vue')
const MintUnitViewer = () => import('@/components/organisms/MintUnitViewer.vue')
const RarityIcon = () => import('@/components/atoms/RarityIcon.vue')
const Numpad = () => import('@/components/organisms/Numpad.vue')
const Summon = () => import('@/components/organisms/Summon.vue')
const MyBalanceBox = () => import('@/components/molecules/MyBalanceBox.vue')

// Nuxt
const nuxtApp = useNuxtApp()
const { $env, $i18n, $cryptoCoinService, $constants, $memoriaService, $util, $toast, $t }: any = nuxtApp

// Data TODO: Refactor to store
const isDeltaTicketGacha = ref(false)
const gmType = ref(0)
const isLoading = ref(false)
const rarity = ref(0)
const setAmount = ref(0)
const memoriaPart = ref(1)
const dropRateModalShown = ref(false)
const processConfimationModalShown = ref(false)
const processResultModalShown = ref(false)
const ticketExchangeConfimationModalShown = ref(false)
const ticketExchangeMode = ref(0)
const lotMemoriaResult = ref({})
const summonResult = ref(<any>{})
const mintParts = ref(<any>[{}, {}, {}])
// bf周年20% OFF
const mintPrice = ref({
  5: 3200,
  4: 800,
  3: 200,
  2: 160,
})
const specialGachaPrice = ref({
  5: 4000,
  4: 1000,
  3: 250,
  2: 200,
})
const gachaPrice = ref({
  1: {
    5: 8000,
    4: 2000,
    3: 500,
    2: 200,
  },
  2: {
    5: 8000,
    4: 2000,
    3: 500,
    2: 200,
  },
  3: {
    5: 8000,
    4: 2000,
    3: 500,
    2: 200,
  },
})
const dropRateList = ref([])
const invalidMintParts = ref(true)
const mintKey = ref(0)
const confirmationAsset = ref({})
const resultAsset = ref({})
const mintExchangeMode = ref(0)
const numpadKey = ref(0)
const contract = ref(null)
const nonce = ref(0)
const isTutorialModalShown = ref(false)
const processId = ref(0)
const processCount = ref(0)
const isBuying = ref(false)

// Computed
const isGacha = computed(() => gmType.value == 0)
const isMint = computed(() => gmType.value == 1)
const isSummon = computed(() => gmType.value == 2)
const isSpecialGacha = computed(() => gmType.value == 3)

const elsPointImgUrl = computed(() => `${$env.cdnUrl}/rsc/item/consume_item/item_40004.png`)
const bpcPointImgUrl = computed(() => `${$env.cdnUrl}/rsc/item/consume_item/item_40005.png`)

const memoriaTicketImgUrl = computed(() => {
  if (isGacha.value && isDeltaTicketGacha.value && rarity.value != 1) return mintTicketImgUrl.value
  if (isGacha.value) return `${$env.cdnUrl}/rsc/item/consume_item/item_40001.png`
  if (isSpecialGacha.value) return `${$env.cdnUrl}/rsc/item/consume_item/item_40007.png`
  return `${$env.cdnUrl}/rsc/item/consume_item/item_40001.png`
})

const mintTicketImgUrl = computed(() => `${$env.cdnUrl}/rsc/item/consume_item/item_40002.png`)
const uMintTicketImgUrl = computed(() => `${$env.cdnUrl}/rsc/item/consume_item/item_40003.png`)

const ticketImgUrl = computed(() => {
  if (isGacha.value || isSpecialGacha.value) return memoriaTicketImgUrl.value
  if (rarity.value == 2) return uMintTicketImgUrl.value
  return mintTicketImgUrl.value
})

const bannerImgUrl = computed(() => {
  const suffix = $i18n.locale == 'ja' ? 'ja' : 'en'
  if (isGacha.value && rarity.value == 5) return `${$env.cdnUrl}/rsc/generate_memoria/L13_${suffix}.jpg`
  if (isGacha.value && rarity.value == 4) return `${$env.cdnUrl}/rsc/generate_memoria/E13_${suffix}.jpg`
  if (isGacha.value && rarity.value == 3) return `${$env.cdnUrl}/rsc/generate_memoria/R13_${suffix}.jpg`
  if (isGacha.value && rarity.value == 2) return `${$env.cdnUrl}/rsc/generate_memoria/U11_${suffix}.jpg`
  if (isGacha.value && rarity.value == 0) return `${$env.cdnUrl}/rsc/generate_memoria/default_${suffix}.png`
  if (isSpecialGacha.value && rarity.value == 5) return `${$env.cdnUrl}/rsc/generate_memoria/L_sp_${suffix}.jpg`
  if (isSpecialGacha.value && rarity.value == 4) return `${$env.cdnUrl}/rsc/generate_memoria/E_sp_${suffix}.jpg`
  if (isSpecialGacha.value && rarity.value == 3) return `${$env.cdnUrl}/rsc/generate_memoria/R_sp_${suffix}.jpg`
  if (isSpecialGacha.value && rarity.value == 2) return `${$env.cdnUrl}/rsc/generate_memoria/U_sp_${suffix}.jpg`
  if (isSpecialGacha.value && rarity.value == 0) return `${$env.cdnUrl}/rsc/generate_memoria/default_sp_${suffix}.png`
  return `${$env.cdnUrl}/rsc/delta_summon/${suffix}.png`
})

const exchangeTicketImgUrl = computed(() => {
  if (isGacha.value) return memoriaTicketImgUrl.value
  if (mintExchangeMode.value == 0) return mintTicketImgUrl.value
  return uMintTicketImgUrl.value
})

const exchangeTicketFtpointImgUrl = computed(() => {
  if (isGacha.value) return elsPointImgUrl.value
  return bpcPointImgUrl.value
})

const price = computed(() => {
  if (isGacha.value) {
    // デルタ召喚チケットで払う場合は1/10で計算
    if (isDeltaTicketGacha.value) return gachaPrice.value[memoriaPart.value][rarity.value] / 10
    return gachaPrice.value[memoriaPart.value][rarity.value]
  } else if (isSpecialGacha.value){
    // 特殊メモリア生成は特別価格
    return specialGachaPrice.value[rarity.value]
  } else {
    return mintPrice.value[rarity.value]
  }
})

const userFt = computed(() => {
  return isGacha.value ? useAddressStore().getElsBalance : useAddressStore().getBpcBalance
})

const userFtTicket = computed(() => {
  return isGacha.value ? useConsumableStore().getMyBalance.elsPoint : useConsumableStore().getMyBalance.bpcPoint
})

const userBalance = computed(() => {
  if (isMint.value && rarity.value == 2) return useConsumableStore().getMyBalance.uMintTicket
  if (isSpecialGacha.value) return useConsumableStore().getMyBalance.specialMemoriaTicket
  if (isGacha.value && isDeltaTicketGacha.value) return useConsumableStore().getMyBalance.mintTicket
  return isGacha.value ? useConsumableStore().getMyBalance.memoriaTicket : useConsumableStore().getMyBalance.mintTicket
})

const typeName = computed(() => {
  if (isGacha.value) {
    return $i18n.locale === 'ja' ? 'メモリア生成' : 'Generate Memoria'
  } else if (rarity.value == 2) {
    return $i18n.locale === 'ja' ? 'U限定デルタ召喚' : 'Uncommon Delta Summon'
  } else {
    return $i18n.locale === 'ja' ? 'デルタ召喚' : 'Delta Summon'
  }
})

const exchangeTicketTypeName = computed(() => {
  if (isGacha.value) return $i18n.locale === 'ja' ? 'メモリア生成' : 'Generate Memoria'
  if (mintExchangeMode.value == 0) return $i18n.locale === 'ja' ? 'デルタ召喚' : 'Delta Summon'
  return $i18n.locale === 'ja' ? 'U限定デルタ召喚' : 'Uncommon Delta Summon'
})

const exchangeTicketFtName = computed(() => {
  if (isGacha.value) return 'ELS'
  return 'BPC'
})

const memoriaPartName = computed(() => {
  if (memoriaPart.value == 1) return $i18n.locale === 'ja' ? 'アバター' : 'Avatar'
  if (memoriaPart.value == 2) return $i18n.locale === 'ja' ? 'ステータス' : 'Status'
  if (memoriaPart.value == 3) return 'BB'
})

const notEnoughBalance = computed(() => userBalance.value < price.value)

const actionText = computed(() => {
  if (isGacha.value) {
    return $i18n.locale === 'ja' ? '生成' : 'Generate'
  } else {
    return $i18n.locale === 'ja' ? '召喚' : 'Summon'
  }
})

const dropRateListSorted = computed(() => {
  let list = useMemoriaMaster()
    .getMemoriaTypes.filter(x => x.active)
    .filter(x => x.memoriaPart == memoriaPart.value && x.rarity == rarity.value && x.isSpecial == isSpecialGacha.value)
  return list.sort((p, c) => {
    if (p.memoriaType < c.memoriaType) return -1
    if (p.memoriaType > c.memoriaType) return 1
    return 0
  })
})

const dropListNotAvailable = computed(() => !dropRateListSorted.value[0])

const CurrentGachaPartName = computed(() => {
  if (memoriaPart.value == 1) return $i18n.locale === 'ja' ? 'アバター' : 'Avatar '
  if (memoriaPart.value == 2) return $i18n.locale === 'ja' ? 'ステータス' : 'Status '
  if (memoriaPart.value == 3) return $i18n.locale === 'ja' ? 'BB' : 'BB '
})

const processingTransaction = computed(() => isBuying.value || processId.value != 0 || processCount.value > 0)

const ticketAmount = computed(() => {
  if (isMint.value && mintExchangeMode.value == 1) {
    return setAmount.value * 10
  }
  return setAmount.value
})

onBeforeMount(async () => {
  isLoading.value = true

  // チケット取得
  await fetch()

  // チュートリアル
  if (!window.localStorage.getItem('bfh:summonTutorial')) {
    isTutorialModalShown.value = true
    window.localStorage.setItem('bfh:summonTutorial', 'true')
  }

  isLoading.value = false
})

// Methods
function changeType(tp: number) {
  gmType.value = tp
  setAmount.value = 0
  numpadKey.value++

  if (isSpecialGacha.value && (rarity.value == 1 || rarity.value == 2)) {
    changeRarity(5)
  }

  if (isSpecialGacha) {
    isDeltaTicketGacha.value = false
  }
}

function changeRarity(r: number) {
  rarity.value = r
  mintKey.value++
  mintParts.value = [{}, {}, {}]
  invalidMintParts.value = true
  setAmount.value = 0
  numpadKey.value++
}

function changeMemoriaPart(p: number) {
  memoriaPart.value = p
}

function cannotExchange(mode: number) {
  if (processingTransaction.value) return true
  if (setAmount.value == 0 || setAmount.value > 99999999) return true
  if (mode == 0 && !useAuthStore().getNetworkBool.isHomeverse) return true
  if (mode == 0) {
    return userFt.value < setAmount.value
  } else {
    return userFtTicket.value < setAmount.value
  }
}

async function process() {
  try {
    if (isGacha.value || isSpecialGacha.value) {
      let lotMemoriaResultRes = await $memoriaService.LotMemoria(
        memoriaPart.value,
        rarity.value,
        isSpecialGacha.value,
        isDeltaTicketGacha.value
      )
      lotMemoriaResult.value = $util.clone(lotMemoriaResultRes)
      processConfimationModalShown.value = false
      $toast.success('Success')
      processResultModalShown.value = true
    } else {
      let summonResultRes = await $memoriaService.Summon(mintParts.value, rarity.value)
      summonResult.value = $util.clone(summonResultRes)
      resultAsset.value = {
        heroType: summonResult.value.heroType,
        attribute: summonResult.value.attribute,
        param: summonResult.value.param,
        passive: summonResult.value.passive,
      }
      invalidMintParts.value = true
      mintKey.value += 1
      processConfimationModalShown.value = false
      $toast.success('Success')
      processResultModalShown.value = true
    }
    await fetch()
  } catch (e) {
    console.log(e)
    $toast.error('Error')
  }
}

async function ftTicketExchange() {
  processCount.value = await $cryptoCoinService.GetUserPurchaseProcessCount($constants.PURCHASE_PROCESS_TYPE.ITEM)
  if (processingTransaction.value) {
    $toast.error($t('common.processingTransaction'))
    return
  }

  isBuying.value = true
  try {
    if (ticketExchangeMode.value == 1) {
      await $memoriaService.TicketExchange(isGacha.value ? 0 : mintExchangeMode.value == 0 ? 1 : 2, setAmount.value)
    } else if (ticketExchangeMode.value == 2) {
      if (userFt.value < setAmount.value) return

      const processType = isGacha.value ? 1 : mintExchangeMode.value == 0 ? 2 : 3
      const tokenAddress = isGacha.value ? useAddressStore().getELSAddress : useAddressStore().getBPCAddress
      const sig = await $cryptoCoinService.GetItemPurchaseSignature(
        setAmount.value,
        processType,
        setAmount.value,
        tokenAddress
      )
      processId.value = sig.processId // 失敗したらこのプロセスを強制的に閉じる

      const web3 = await useWalletStore().getWeb3
      const bigIntPrice = web3.utils.toWei(setAmount.value.toString(), 'ether')

      // ユーザーの許可Approve、一回のみ
      const tokenContract = new web3.eth.Contract(FtAbi as any, tokenAddress)
      const allowance = await tokenContract.methods
        .allowance(useUserStore().me.eth, useAddressStore().getItemPurchaseAddress)
        .call()
      if (BigInt(allowance) < BigInt(bigIntPrice)) {
        await tokenContract.methods
          .approve(useAddressStore().getItemPurchaseAddress, useAddressStore().getBigIntMax)
          .send({ from: useUserStore().me.eth })
      }

      // チケット購入
      await contract.value.methods
        .purchase(
          nonce.value,
          sig.processId,
          bigIntPrice,
          processType,
          setAmount.value,
          tokenAddress,
          sig.multiSignature.sigv,
          sig.multiSignature.sigr,
          sig.multiSignature.sigs
        )
        .send({ from: useUserStore().me.eth })

      // トランザクション確認
      await $cryptoCoinService.UserCheckPurchaseProcess(processId.value, $constants.PURCHASE_PROCESS_TYPE.ITEM)
      nonce.value++
    }

    await fetch()
    isBuying.value = false
    processId.value = 0
    $toast.success('Success')
  } catch (e) {
    if (processId.value != 0 && e.code == 4001) {
      // 強制的に閉じる
      await $cryptoCoinService.ForceCloseItemPurchaseProcess(processId.value)
    }
    console.log(e)
    isBuying.value = false
    processId.value = 0
    $toast.error(e.message)
  }

  setAmount.value = 0
  numpadKey.value++
  ticketExchangeConfimationModalShown.value = false
}

async function fetch() {
  await useConsumableStore().fetchConsumeMyItems()
  if (useAuthStore().getNetworkBool.isHomeverse) {
    const web3 = await useWalletStore().getWeb3
    contract.value = new web3.eth.Contract(ItemPurchaseAbi as any, useAddressStore().getItemPurchaseAddress)
    nonce.value = await contract.value.methods.nonces(useUserStore().me.eth).call()
    await useAddressStore().fetchBpcBalance()
    await useAddressStore().fetchElsBalance()
  }

  processCount.value = await $cryptoCoinService.GetUserPurchaseProcessCount($constants.PURCHASE_PROCESS_TYPE.ITEM)
}

function updateMintParts(parts) {
  mintParts.value = parts

  if (
    mintParts.value[0].rarity == rarity.value &&
    mintParts.value[1].rarity == rarity.value &&
    mintParts.value[2].rarity == rarity.value &&
    mintParts.value[0].memoriaPart == 1 &&
    mintParts.value[1].memoriaPart == 2 &&
    mintParts.value[2].memoriaPart == 3
  ) {
    invalidMintParts.value = false
  } else {
    invalidMintParts.value = true
  }
}

function openProcessConfirmationModal() {
  confirmationAsset.value = {
    heroType: mintParts.value[0].heroType,
    attribute: mintParts.value[0].attribute,
    param: mintParts.value[1].param,
    passive: mintParts.value[2].skillId,
  }
  processConfimationModalShown.value = true
}

function roundOffPercentage(dropPercentage) {
  if (!dropPercentage) return 0
  return Number(dropPercentage.toFixed(6))
}
</script>

<template>
  <div class="heroesShopPage">
    <client-only>
      <div class="gmTypeSelector">
        <button @click="changeType(0)" :class="{ active: gmType === 0 }">{{ $t('market.heroes.gacha') }}</button>
        <button @click="changeType(3)" :class="{ active: gmType === 3 }">{{ $t('market.heroes.specialGacha') }}</button>
        <button @click="changeType(1)" :class="{ active: gmType === 1 }">
          {{ $t('market.heroes.mint') }}
        </button>
        <button @click="changeType(2)" :class="{ active: gmType === 2 }">
          {{ $t('market.heroes.hero') }}
        </button>
      </div>

      <div class="infoBox">
        <fa-icon class="icon_bikkuri" :icon="['fas', 'exclamation-circle']" size="3x" />
        <div v-if="isGacha || isMint || isSpecialGacha" class="txtBox">
          <Nl2br tag="p" :text="$t('market.heroes.summonMintNoticeBody')" />
        </div>
        <div v-if="isSummon" class="txtBox">
          <p>{{ $t('market.heroes.heroNoticeBody1') }}</p>
          <span class="text-yellow-500">
            <Nl2br tag="p" :text="$t('market.heroes.heroNoticeBody2')" />
          </span>
          <Nl2br tag="p" :text="$t('market.heroes.heroNoticeBody3')" />
        </div>
      </div>

      <span v-if="isLoading" class="loadingBox w-full text-center block">
        <fa-icon :icon="['fal', 'spinner']" spin size="3x" />
      </span>

      <div v-if="!isLoading && !isSummon" class="mb-2 flex">
        <MyBalanceBox v-if="isGacha" :mode="'memoriaTicket'" />
        <MyBalanceBox v-if="isGacha && rarity !== 1" class="ml-4" :mode="'mintTicket'" />
        <MyBalanceBox v-if="isSpecialGacha" :mode="'specialMemoriaTicket'" />
        <MyBalanceBox v-if="isGacha && rarity === 1" class="ml-4" :mode="'elsPoint'" />
        <MyBalanceBox v-if="isGacha && rarity === 1" class="ml-4" :mode="'els'" />
        <MyBalanceBox v-if="isMint && rarity !== 1" :mode="'mintTicket'" />
        <MyBalanceBox v-if="isMint && rarity !== 1" class="ml-4" :mode="'uMintTicket'" />
        <MyBalanceBox v-if="isMint && rarity === 1 && mintExchangeMode === 0" :mode="'mintTicket'" />
        <MyBalanceBox v-if="isMint && rarity === 1 && mintExchangeMode === 1" :mode="'uMintTicket'" />
        <MyBalanceBox v-if="isMint && rarity === 1" class="ml-4" :mode="'bpcPoint'" />
        <MyBalanceBox v-if="isMint && rarity === 1" class="ml-4" :mode="'bpc'" />
      </div>

      <div v-if="!isLoading && !isSummon" class="gm">
        <div v-if="rarity === 0" class="tabExplain">
          <p>{{ $i18n.locale === 'ja' ? '▼ レアリティを選択してください ▼' : '▼ Please select a rarity ▼' }}</p>
        </div>
        <div class="rarityButton">
          <button @click="changeRarity(5)" :class="{ active: rarity === 5 }">
            <div class="buttonContent">
              <RarityIcon :asset="{ rarity: 5 }" />
              Legendary
            </div>
          </button>
          <button @click="changeRarity(4)" :class="{ active: rarity === 4 }">
            <div class="buttonContent">
              <RarityIcon :asset="{ rarity: 4 }" />
              Epic
            </div>
          </button>
          <button @click="changeRarity(3)" :class="{ active: rarity === 3 }">
            <div class="buttonContent">
              <RarityIcon :asset="{ rarity: 3 }" />
              Rare
            </div>
          </button>
          <button @click="changeRarity(2)" :class="{ active: rarity === 2 }" :disabled="isSpecialGacha">
            <div class="buttonContent">
              <RarityIcon :asset="{ rarity: 2 }" />
              Uncommon
            </div>
          </button>
          <button @click="changeRarity(1)" :class="{ active: rarity === 1 }" :disabled="isSpecialGacha">
            <div class="buttonContent">
              <span v-if="isGacha || isSpecialGacha">{{ $t('market.heroes.buyMemoriaTicket') }}</span>
              <span v-else>{{ $t('market.heroes.buyMintTicket') }}</span>
            </div>
          </button>
        </div>
        <div v-if="rarity !== 1" class="bannerImg">
          <img :src="bannerImgUrl" />
        </div>
        <div v-if="isMint && rarity === 1" class="mintExchangeMode">
          <div class="line"></div>
          <br />
          <button @click="mintExchangeMode = 0" :class="{ active: mintExchangeMode === 0 }">
            <div class="buttonContent">
              <RarityIcon :asset="{ rarity: 5 }" />
              <RarityIcon :asset="{ rarity: 4 }" />
              <RarityIcon :asset="{ rarity: 3 }" />
              {{ $t('market.heroes.mintTicket') }}
            </div>
          </button>
          <button @click="mintExchangeMode = 1" :class="{ active: mintExchangeMode === 1 }">
            <div class="buttonContent">
              <RarityIcon :asset="{ rarity: 2 }" />
              {{ $t('market.heroes.uMintTicket') }}
            </div>
          </button>
        </div>
        <div v-if="rarity === 1" class="gmTop">
          <br />
          <div class="gmTopInfo">
            <img :src="exchangeTicketImgUrl" class="w-12 h-12" />
            <span>{{ $t('market.heroes.gmInfo', { typeName: exchangeTicketTypeName }) }}</span>
            <p>
              {{
                $t('market.heroes.ftExchangeInfo', {
                  ftName: exchangeTicketFtName,
                  ticketAmount: mintExchangeMode == 1 ? 10 : 1,
                })
              }}
            </p>
          </div>
          <br />
          <div class="amountSelect">
            <div class="text-center" v-if="setAmount == 0">
              <p class="font-bold text-white">
                {{ $t('market.heroes.pleaseEnterFtAmount', { ftName: exchangeTicketFtName }) }}
              </p>
            </div>
            <div class="text-center" v-else>
              <p class="font-bold text-white">
                {{ $t('market.heroes.ftExchangeAmountNeeded', { ftName: exchangeTicketFtName, amount: setAmount }) }}
              </p>
              <p>{{ $t('market.heroes.ticketAmountGet', { amount: ticketAmount }) }}</p>
            </div>
            <Numpad @update="num => (setAmount = num)" :key="numpadKey" />
          </div>
          <p v-if="processingTransaction" class="text-yellow-500 text-center">
            {{ $t('common.processingTransaction') }}
          </p>
          <div class="exchangeDetail">
            <button
              class="active"
              @click="
                ticketExchangeMode = 1
                ticketExchangeConfimationModalShown = true
              "
              :disabled="cannotExchange(1)"
            >
              <fa-icon v-if="processingTransaction" :icon="['fal', 'spinner']" spin />
              {{ exchangeTicketFtName }}{{ $i18n.locale === 'ja' ? 'ポイントで購入' : ' Point Exchange' }}
            </button>
            <button
              class="active"
              @click="
                ticketExchangeMode = 2
                ticketExchangeConfimationModalShown = true
              "
              :disabled="cannotExchange(0)"
            >
              <fa-icon v-if="processingTransaction" :icon="['fal', 'spinner']" spin />
              <img src="/images/i_icon_hmv.png" class="h-full p-1" />
              {{ exchangeTicketFtName }}{{ $i18n.locale === 'ja' ? 'で購入' : ' Exchange' }}
            </button>
          </div>
        </div>
        <div v-if="(isGacha || isSpecialGacha) && rarity !== 1 && rarity !== 0" class="gmGacha">
          <br />
          <div class="memoriaPartButton">
            <button @click="changeMemoriaPart(1)" :class="{ active: memoriaPart === 1 }">
              {{ $t('parts.tab.avatar') }}
            </button>
            <button @click="changeMemoriaPart(2)" :class="{ active: memoriaPart === 2 }">
              {{ $t('parts.tab.status') }}
            </button>
            <button @click="changeMemoriaPart(3)" :class="{ active: memoriaPart === 3 }">
              {{ $t('parts.tab.bb') }}
            </button>
          </div>
          <div class="gmGachaDetail">
            <div v-if="!isSpecialGacha" class="gmMemoriaTicketTypeTab flex mx-auto flex-col gap-y-2 gap-x-16">
              <button @click="isDeltaTicketGacha = false" class="!w-52 !m-0" :class="{ active: !isDeltaTicketGacha }">
                {{ $i18n.locale === 'ja' ? 'メモリア生成チケット' : 'Generate Memoria Tickets' }}
              </button>
              <button @click="isDeltaTicketGacha = true" class="!w-52" :class="{ active: isDeltaTicketGacha }">
                {{ $i18n.locale === 'ja' ? 'デルタ召喚チケット' : 'Delta Summon Tickets' }}
              </button>
            </div>
            <div>
              <span class="text-xl">{{ $t('market.heroes.numberOfTypes', { types: dropRateListSorted.length }) }}</span>
              <button class="dropRateButton" @click="dropRateModalShown = true">
                {{ $t('market.heroes.dropRateInfo') }}
              </button>
            </div>
            <div>
              <span>{{ $i18n.locale === 'ja' ? '一回：' : '1 draw: ' }}</span>
              <img :src="memoriaTicketImgUrl" class="w-8 h-8" />
              <span v-if="$i18n.locale !== 'ja'">x</span>
              <span>{{ price }}</span>
              <span v-if="$i18n.locale === 'ja'">枚</span>
            </div>
            <p v-if="isGacha && dropListNotAvailable">{{ $t('market.heroes.dropListNotAvailable') }}</p>
            <button
              class="actionButton"
              v-else
              :disabled="notEnoughBalance || dropListNotAvailable"
              @click="openProcessConfirmationModal()"
            >
              {{ CurrentGachaPartName }}{{ $i18n.locale === 'ja' ? '生成' : 'Generate' }}
            </button>
          </div>
        </div>
        <div v-if="isMint && rarity !== 1 && rarity !== 0" class="gmMintHeader">
          <div>
            <img :src="ticketImgUrl" class="w-8 h-8" />
            <span>{{ $t('market.heroes.footerAmountNeeded', { price: price }) }}</span>
          </div>
          <button :disabled="notEnoughBalance || invalidMintParts" @click="openProcessConfirmationModal()">
            {{ actionText }}
          </button>
        </div>
        <div v-if="isMint && rarity !== 1 && rarity !== 0" class="gmMint">
          <MintSelector @updateMintParts="updateMintParts" :rarity="rarity" :key="mintKey" />
        </div>
      </div>
    </client-only>

    <div v-if="!isLoading && isSummon" class="summon">
      <Summon />
    </div>

    <modal v-if="dropRateModalShown" @modal-close="dropRateModalShown = false">
      <template #header>
        <h2>{{ $t('market.heroes.dropRateTitle') }}</h2>
      </template>
      <div class="dropRateInfo">
        <Nl2br
          tag="p"
          :text="
            $t('market.heroes.dropRateDescription', {
              dropPercentage: roundOffPercentage(dropRateListSorted[0]?.dropPercentage),
            })
          "
        />
      </div>
      <div v-if="!dropListNotAvailable" class="dropRateList">
        <DropRateList :list="dropRateListSorted" />
      </div>
      <div v-else>
        <br />
        <p>{{ $t('market.heroes.dropListNotAvailable') }}</p>
      </div>
    </modal>

    <modal v-if="processConfimationModalShown" @modal-close="processConfimationModalShown = false">
      <template #header>
        <h2>{{ $t('market.heroes.processConfimationTitle') }}</h2>
      </template>
      <div class="processConfimationInfo">
        <Nl2br
          v-if="isGacha || isSpecialGacha"
          tag="p"
          :text="$t('market.heroes.processConfimationGachaDescription', { part: memoriaPartName })"
        />
        <div v-else class="mintUnitViewerBody">
          <Nl2br
            tag="p"
            :text="$t('market.heroes.processConfimationMintDescription', { price: price, typeName: typeName })"
          />
          <MintUnitViewer :asset="confirmationAsset" />
        </div>
        <br />
        <div class="text-center">
          <img :src="ticketImgUrl" class="w-8 h-8" />
          <span>{{ userBalance + ' → ' + (userBalance - price) }}</span>
        </div>
      </div>
      <template #footer>
        <button @click="processConfimationModalShown = false">{{ $t('common.cancel') }}</button>
        <button @click.once="process()">{{ $t('common.ok') }}</button>
      </template>
    </modal>

    <modal v-if="processResultModalShown" @modal-close="processResultModalShown = false">
      <template #header>
        <h2 v-if="isGacha || isSpecialGacha">{{ $t('market.heroes.processResultGachaTitle') }}</h2>
        <h2 v-else>{{ $t('market.heroes.processResultMintTitle') }}</h2>
      </template>
      <div class="processResultInfo">
        <h3>
          {{
            isGacha || isSpecialGacha
              ? $t('market.heroes.processResultGachaDescription')
              : $t('market.heroes.processResultMintDescription')
          }}
        </h3>
        <LotMemoriaResult v-if="isGacha || isSpecialGacha" :memoria="lotMemoriaResult" />
        <div v-else class="mintUnitViewerBody">
          <MintUnitViewer :asset="resultAsset" />
        </div>
      </div>
      <template #footerOneBtn>
        <button @click="processResultModalShown = false">{{ $t('common.ok') }}</button>
      </template>
    </modal>

    <modal
      class="ticketExchangeConfimationModal"
      v-if="ticketExchangeConfimationModalShown"
      @modal-close="ticketExchangeConfimationModalShown = false"
    >
      <template #header>
        <h2>{{ $t('market.heroes.ticketExchangeConfimationTitle') }}</h2>
      </template>
      <div class="ticketExchangeConfimationInfo">
        <Nl2br
          tag="p"
          :text="
            $t('market.heroes.ticketExchangeConfimationDescription', {
              ft: isGacha ? 'ELS' : 'BPC',
              type: typeName,
            })
          "
        />
        <div class="ticketExchangeConfimationInfoDetail">
          <img v-if="ticketExchangeMode === 1" :src="exchangeTicketFtpointImgUrl" class="w-12 h-12" />
          <img v-else-if="isGacha" src="/images/i_icon_els.png" class="w-12 h-12" />
          <img v-else src="/images/i_icon_bpc.png" class="w-12 h-12" />
          <p>{{ setAmount }}{{ $i18n.locale === 'ja' ? '枚' : '' }}</p>
          <p class="arrow">↓</p>
          <img :src="exchangeTicketImgUrl" class="w-12 h-12" />
          <p>
            {{ exchangeTicketTypeName }}{{ $i18n.locale === 'ja' ? 'チケット' : ' Tickets' }} x{{ ticketAmount
            }}{{ $i18n.locale === 'ja' ? '枚' : '' }}
          </p>
        </div>
      </div>
      <template #footer>
        <button @click="ticketExchangeConfimationModalShown = false">{{ $t('common.cancel') }}</button>
        <button
          @click.once="
            ftTicketExchange()
            ticketExchangeConfimationModalShown = false
          "
        >
          {{ $t('common.ok') }}
        </button>
      </template>
    </modal>

    <modal v-if="isTutorialModalShown" @close="isTutorialModalShown = false">
      <template #header>
        <h3>{{ $t('tutorial.summon.title') }}</h3>
      </template>
      <div class="tutorialModalShown__body">
        <img :src="`${$env.cdnUrl}/rsc/tutorial/summon_${$i18n.locale}.png`" class="mb-4" />
        <Nl2br tag="p" :text="$t('tutorial.summon.detail')" />
      </div>
      <template #footerOneBtn>
        <button @click="isTutorialModalShown = false">{{ $t('common.ok') }}</button>
      </template>
    </modal>
  </div>
</template>

<style lang="scss" scoped>
.gm {
  .rarityButton {
    font-size: 0;

    @media (max-width: $breakpoint) {
      display: grid;
      grid-template-columns: 1fr 1fr;
      row-gap: 0.5rem;

      button {
        width: 90%;
      }

      button:last-of-type {
        width: 50%;
        grid-column-start: 1;
        grid-column-end: 3;
        margin: 0 auto;
      }
    }

    button {
      @extend %tab--five;
    }

    .buttonContent {
      display: flex;
      justify-content: center;
      align-items: center;

      .rarityIcon {
        margin-right: 0.25rem;
      }
    }
  }

  .selectArea {
    @extend %select;
  }

  .tabExplain {
    font-weight: 700;
    text-align: center;
    margin-bottom: 1rem;
  }

  .bannerImg {
    margin-top: 1rem;
    display: grid;
    place-items: center;
  }

  .mintExchangeMode {
    .line {
      height: 1rem;
      border-bottom: 1px solid #a88c59;
    }

    button {
      @extend %tab--two;

      @media (max-width: $breakpoint) {
        height: 3rem;
      }
    }

    .buttonContent {
      display: flex;
      justify-content: center;
      align-items: center;

      .rarityIcon {
        margin-right: 0.25rem;
      }
    }
  }

  .gmTop {
    background: #4a381c;
    border: 1px solid #a88c59;
    padding: 10px 10px 10px 10px;
    margin: 15px auto 15px;
    color: #ffeece;

    .gmTopInfo {
      text-align: center;
    }

    .amountSelect {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .exchangeDetail {
      display: grid;
      grid-template-rows: 1fr 1fr;
      grid-template-columns: 1fr 1fr;
      place-items: center;
      row-gap: 1rem;

      p {
        grid-column-start: 1;
        grid-column-end: 3;
      }

      button {
        @extend %btn-primary;
        width: 9rem;
      }

      @media (min-width: $breakpoint) {
        padding: 0 20rem;
      }
    }
  }

  .gmGacha {
    .memoriaPartButton {
      font-size: 0;

      button {
        @extend %tab--three;
      }
    }

    .gmGachaDetail {
      display: grid;
      place-content: center;
      row-gap: 1rem;
      text-align: center;
      background: #1d1e21;
      margin-top: 1rem;
      border: 1px solid #a88c59;
      padding: 1rem;

      p {
        font-size: 1.25rem;
        margin: auto 0;
      }

      .gmMemoriaTicketTypeTab {
        button {
          @extend %tab--two;
        }

        .active {
          border: 2px solid #a88c59 !important;
          border-radius: 50px !important;
        }

        @media (min-width: $breakpoint) {
          flex-direction: row;
        }
      }

      .dropRateButton {
        text-decoration: underline;
        color: #2d98eb;
        text-align: left;
        font-size: 1.25rem;
      }

      .actionButton {
        @extend %btn-primary;
        margin: 0 auto;
      }
    }
  }

  .gmMintHeader {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    place-items: center;
    padding: 1rem;
    background: #1d1e21;
    border: 1px solid #a88c59;
    margin: 1rem 0;
    text-align: center;

    button {
      @extend %btn-primary;
    }
  }
}

.gmTypeSelector {
  margin-top: 10px;
  text-align: center;
  width: 100%;
  height: 45px;
  font-size: 0;

  button {
    @extend %tab--four;
  }
}

.infoBox {
  width: 100%;
  background: #1d1e21;
  border: 1px solid #a88c59;
  margin: 0 auto 15px;
  padding: 10px 10px 10px 0;
  display: grid;
  grid-template-columns: 4em 1fr;
  justify-content: center;
  align-items: center;
  color: #d2b887;

  p {
    font-size: 0.9rem;
    font-size: 12px;
  }

  p:first-of-type {
    margin-bottom: 4px;
  }
}

.icon_bikkuri {
  margin: 0 auto;
  width: inherit;
  font-size: 2.5em;
}

.txtBox {
  line-height: 1.3;
}

@media (min-width: 768px) {
  .infoBox {
    p {
      font-size: 14px;
      grid-template-columns: 5em 1fr;
    }

    p:first-of-type {
      margin-bottom: 6px;
    }
  }
}

h4 {
  width: 100%;
  -webkit-box-align: center;
  align-items: center;
  color: #ffa500;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 1rem;
  margin-bottom: 1rem;
  font-size: 1rem;

  @media (min-width: $breakpoint) {
    font-size: 1.2rem;
  }

  &:after {
    background: #ffa500;
    content: '';
    display: block;
    height: 1px;
    width: 100%;
  }
}

.ticketExchangeConfimationModal {
  .ticketExchangeConfimationInfoDetail {
    display: grid;
    place-items: center;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr;
    padding: 2rem 0;

    .arrow {
      grid-column-start: 1;
      grid-column-end: 3;
      font-size: 1.5rem;
    }

    @media (min-width: $breakpoint) {
      padding: 2rem 5rem;
    }
  }
}

.processResultInfo {
  h3:nth-of-type(1) {
    text-align: center;
  }
}

.processConfimationInfo {
  p:first-of-type {
    text-align: center;
  }
}
</style>
